<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card class="q-dialog-plugin">
      <q-card-section class="row">
        <div
          class="text-h6 ellipsis col"
        >
          {{ $t('sessions.parentSessionCard.textTitle') }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-card-section class="row items-center justify-center q-gutter-md">
        <div
          v-for="reactionOption in reactionOptions"
          :key="reactionOption.value"
        >
          <transition
            appear
            enter-active-class="animated bounceIn slower delay-1s"
          >
            <q-btn
              v-ripple
              clickable
              round
              :icon="reactionOption.icon"
              size="md"
              :color="reactionOption.value === selectedReaction ? 'primary': 'grey-4'"
              :class="'reaction-' + reactionOption.value"
              @click="onSelectReaction(reactionOption)"
            >
              <q-tooltip>
                {{ reactionOption.tooltip }}
              </q-tooltip>
            </q-btn>
          </transition>
        </div>
      </q-card-section>
      <q-card-section>
        <tiptap-input
          v-model="sessionComment"
          class="full-width"
          :session-id="session.id"
          :client-id="session.client"
          :placeholder="$t('sessions.parentSessionCard.placeholderSessionComments')"
          autogrow
          outlined
        />
      </q-card-section>
      <!-- buttons example -->
      <q-card-actions class="row">
        <file-attachment
          ref="attachment"
          :disable="offlineMode"
          :color="videoAttached?'secondary':'primary'"
          :icon="videoAttached?'check':'video_library'"
          size="md"
          flat
          :file-path="'/sessions/' + newSessionId + '/'"
          :label="videoAttached
            ? $t('sessions.parentSessionCard.buttonVideoAttached')
            : $t('sessions.parentSessionCard.buttonAttachVideo')"
          lazy
          :show-warning="false"
          @click="onAttachVideo"
        />
        <q-space />
        <q-btn
          color="secondary"
          :label="$t('common.buttonCancel')"
          flat
          @click="onCancelClick"
        />
        <q-btn
          color="primary"
          :loading="showSpinner"
          :label="$t('common.buttonSave')"
          :disable="!canSubmit"
          flat
          data-e2e="button-save"
          @click="onOKClick"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import FileAttachment from 'components/FileAttachment';
import TiptapInput from 'components/UI/TiptapInput.vue';
import { Timestamp } from 'firebase/firestore';
import { mapActions, mapState } from 'pinia';
import { useTime } from 'src/composables/time';
import MessagesDB from 'src/fb/messages-db';
import SessionsDB from 'src/fb/sessions-db';
import Activities from 'src/mixins/Activities';
import Interactions from 'src/mixins/Interactions';
import MoodReactions from 'src/mixins/MoodReactions';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { sessions } from 'src/store/sessions';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'AddActivity',

  components: { TiptapInput, FileAttachment },

  mixins: [Activities, MoodReactions, Interactions],

  props: {
    // ...your custom props
    session: {
      type: Object,
      required: true,
    },
    sessionName: {
      type: String,
      required: false,
      default: null,
    },
  },

  emits: ['hide', 'ok'],

  setup() {
    const { addToDate } = useTime();
    return { addToDate };
  },

  data() {
    return {
      showSpinner: false,
      newSessionId: null,
      sessionComment: {
        text: '',
        html: '',
      },
      selectedReaction: null,
      videoAttached: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'currentClient', 'connectedClinic']),
    ...mapState(network, ['offlineMode']),

    canSubmit() {
      return this.selectedReaction !== null;
    }, // canSubmit()

    sessionCommentTrimmed() {
      return this.sessionComment.text.trim();
    }, // sessionCommentTrimmed()
  },

  methods: {
    ...mapActions(sessions, ['duplicateSession']),

    // following method is REQUIRED
    // (don't change its name --> "show")
    show() {
      this.$refs.dialog.show();
    },

    // following method is REQUIRED
    // (don't change its name --> "hide")
    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onDialogHide() {
      // required to be emitted
      // when QDialog emits "hide" event
      this.$emit('hide');
    },

    async onOKClick() {
      await this.createSession();
      await this.addActivity('sessionFinished', true, null, null, true, 'session', {
        originalSessionId: this.session.id,
        id: this.newSessionId,
      });
      // on OK, it is REQUIRED to
      // emit "ok" event (with optional payload)
      // before hiding the QDialog
      this.$emit('ok', this.selectedReaction);
      // or with payload: this.$emit('ok', { ... })

      // then hiding dialog
      this.hide();
    },

    onCancelClick() {
      // we just need to hide dialog
      this.hide();
    },

    onSelectReaction(reaction) {
      this.selectedReaction = reaction.value;
    },

    onAttachVideo() {
      this.videoAttached = true;
    },

    async createSession() {
      this.showSpinner = true;
      try {
        const runTimestamp = new Date();
        const finishTimestamp = this.addToDate(runTimestamp, { minutes: 1 });
        const sessionProps = {
          shuffle: false,
          originalSessionId: this.session.id,
          currentProgram: null,
          runBy: this.userDetails.userId,
          runByName: this.userDetails.name,
          runTimestamp: Timestamp.fromDate(runTimestamp),
          finishTimestamp: Timestamp.fromDate(finishTimestamp),
          supervisedBy: null,
          supervisedByName: null,
          moodReaction: this.selectedReaction,
          sessionName: this.sessionName || this.session.sessionName || '',
          sessionType: this.session.sessionType,
          duration: 60,
          hasTimecodedData: true,
          status: 'done',
          version: 2,
        };
        // Create a session
        this.newSessionId = await this.duplicateSession({
          sessionId: this.session.id,
          sessionProps,
        });

        const sessionsDb = new SessionsDB();
        await Promise.all([
          sessionsDb.endSession({ id: this.newSessionId, ...sessionProps }, this.connectedClinic),
          // Initiate file upload
          this.$refs.attachment.submit({ id: this.newSessionId, ...sessionProps }),
          this.onAddComment(this.newSessionId),
        ]);
      } catch (err) {
        this.notifyNegative(err.message);
        logEvent(err.message);
        console.error(err);
      } finally {
        this.showSpinner = false;
      }
    }, // createSession()

    async onAddComment(newId) {
      if (!this.sessionCommentTrimmed) {
        return;
      }

      const messagesDb = new MessagesDB();
      try {
        const messageObj = {
          author: this.userDetails.userId,
          authorName: this.userDetails.name,
          thread: `session_${newId}`,
          threadSubPath: newId,
          origin: 'session',
          session: newId,
          sessionTimeOffset: null,
          message: this.sessionComment.text,
          messageHtml: this.sessionComment.html,
          clinic: this.userDetails.clinic,
          client: this.currentClient.id,
        };
        await messagesDb.create(messageObj);
      } finally {
        this.sessionComment = {
          text: '',
          html: '',
        };
      }
    }, // onAddComment()
  },
};
</script>
<style lang="scss" scoped>

.reaction-heart {
  animation-delay: 100ms;
}

.reaction-laugh {
  animation-delay: 200ms;
}

.reaction-smile {
  animation-delay: 300ms;
}

.reaction-meh {
  animation-delay: 400ms;
}

.reaction-sad {
  animation-delay: 500ms;
}

.reaction-crossbones {
  animation-delay: 600ms;
}

</style>
